// The control-event vocabulary (P0.2) — Architecture §3, FR-38.
//
// This file is types plus pure string functions. The bus itself, the mapping layer, and the
// registry of concrete control ids belong to /control and arrive with the features that need
// them. What is fixed here is the *scheme*: the shape ids take, and what an event carries.

import type { AudioTime } from './branded';

/**
 * The top-level partitions of the control namespace (Architecture §3's examples:
 * `deck.a.pitch`, `mixer.crossfader`, `pads.bank.0.pad.7`, `fx.a.slot.0.wet`).
 *
 * Deliberately not exhaustive over the product — domains are added as features land. What
 * matters at P0.2 is that every id is domain-prefixed, because Architecture §3 has consumers
 * "subscribe by id or namespace" and the domain is the coarsest namespace.
 */
export type ControlDomain = 'deck' | 'mixer' | 'pads' | 'fx';

export const CONTROL_DOMAINS: readonly ControlDomain[] = ['deck', 'mixer', 'pads', 'fx'];

/**
 * A stable, namespaced identifier for a performable control — `deck.a.pitch`,
 * `deck.b.hotcue.3`, `mixer.crossfader`, `pads.bank.0.pad.7`, `fx.a.slot.0.wet`.
 *
 * Grammar: two or more `.`-separated segments of `[a-z0-9]`, the first being a ControlDomain.
 * The tail stays open at P0.2 — pinning every leaf now would mean inventing the whole control
 * surface (FR-01..FR-25) three prompts before anything makes a sound.
 *
 * Ids are strings because they are the *control plane* (Architecture §3): UI reactions,
 * mapping UI, the cheat sheet. They are never what crosses into the audio thread — the
 * orchestrator translates sound-affecting events into numeric engine commands and writes
 * those to the command ring (see rings.ts). Strings would mean allocating on a thread that
 * must not allocate.
 */
export type ControlId = `${ControlDomain}.${string}`;

/** The two decks (FR-01). */
export type DeckId = 'a' | 'b';

/**
 * Both decks, in order, so a table over them is written once rather than per deck (P3.7).
 *
 * Its value is that a mapping table built by iterating this cannot be one row short — which is the
 * exact shape of the omission P3.7 walked into twice, in `transport-controls.ts` and in
 * `control-mapping.ts`. Deck A stays first: it is the left-hand deck in every layout and the one a
 * single-deck path (`/bench`, the e2e fixture) means when it says "the deck".
 */
export const DECK_IDS: readonly DeckId[] = ['a', 'b'];

/**
 * Where an event came from.
 *
 * `midi` exists from day one and goes unused until FF-1. D4 requires the architecture be
 * MIDI-ready, and Architecture §3 is explicit that a MIDI source "emits the same
 * ControlEvents and reuses the entire mapping layer" — so the seam is a union member, not a
 * TODO. `internal` covers events the app raises itself (onboarding, automation).
 */
export type ControlSource = 'keyboard' | 'pointer' | 'midi' | 'internal';

/** Architecture §3: `ControlEvent { id, value, timestamp, source }`. */
export interface ControlEvent {
  readonly id: ControlId;
  /** Normalised 0..1 for continuous controls; 0 or 1 for momentary ones. */
  readonly value: number;
  /** AudioContext time, so events stay orderable against audio (Architecture §4.5). */
  readonly timestamp: AudioTime;
  readonly source: ControlSource;
}

export interface ParsedControlId {
  readonly domain: ControlDomain;
  readonly segments: readonly string[];
}

const SEGMENT_PATTERN = /^[a-z0-9]+$/;

function isControlDomain(value: string): value is ControlDomain {
  return (CONTROL_DOMAINS as readonly string[]).includes(value);
}

/** Splits an id into its domain and segments, or returns null if it breaks the grammar. */
export function parseControlId(raw: string): ParsedControlId | null {
  const segments = raw.split('.');
  if (segments.length < 2) {
    return null;
  }
  for (const segment of segments) {
    if (!SEGMENT_PATTERN.test(segment)) {
      return null;
    }
  }

  const domain = segments[0];
  if (domain === undefined || !isControlDomain(domain)) {
    return null;
  }

  return { domain, segments };
}

export function isControlId(raw: string): raw is ControlId {
  return parseControlId(raw) !== null;
}

/**
 * Namespace subscription (Architecture §3: "Consumers subscribe by id or namespace").
 *
 * Matches on segment boundaries, not characters: `deck.a` covers `deck.a.pitch` but must not
 * covertly swallow `deck.ab.pitch`. A plain `startsWith` would, which is why this exists.
 */
export function controlIdInNamespace(id: ControlId, namespace: string): boolean {
  return id === namespace || id.startsWith(`${namespace}.`);
}
